import React from 'react'
import { Outlet, useParams, useNavigate, useLocation } from 'react-router-dom'
import LanguageChangeIcon from '../assets/images/backgrounds/language-change-icon.png'
import { Footer } from '../components/ui'
import { useTenant } from '../context/TenantContext'
import { useLanguage } from '../context/LanguageContext'
import { useClient } from '../context/ClientContext'
import { useNPS } from '../hooks/useNPS'
import FeedbackModal from '../components/ui/FeedbackModal'
import Loader from '../components/ui/loader'
import BottomAction from '../components/ui/bottom-action'
import GenericNotFound from './GenericNotFound'
import { Settings, Search } from 'lucide-react'

import {
  UserIcon,
  ShoppingBagIcon,
  QuestionMark,
  ListIcon,
  DoubleCard,
  MonitorCheckIcon,
  SignOut,
  HamburgerIcon,
  CashOnHand,
} from '../components/icons'
import Navigation from '../components/ui/navigation'
import { isFaqEnabled } from '../utils/tenantFeatures'
import { getCloudfrontAssetUrl } from '../utils/imageUtils'

const MainLayout = () => {
  const {
    tenant,
    loadTenant,
    isLoading: tenantLoading,
    error: tenantError,
  } = useTenant()
  const { languageCode, changeLanguage, t, isChangingLanguage } = useLanguage()
  const { isAuthenticated, logout, client } = useClient()
  const { tenanturlname } = useParams()
  const navigate = useNavigate()
  const showFaq = isFaqEnabled(tenant)
  const [drawerOpen, setDrawerOpen] = React.useState(false)
  const [showMobileSearch, setShowMobileSearch] = React.useState(false)
  const [searchQuery, setSearchQuery] = React.useState('')
  const closeBtnRef = React.useRef(null)
  const { checkEligibility } = useNPS()
  const [showNPS, setShowNPS] = React.useState(false)
  const [npsChecked, setNpsChecked] = React.useState(false)
  const mobileSearchRef = React.useRef(null)
  const permanentSearchInputRef = React.useRef(null)
  const location = useLocation()
  const tenantLogoUrl = getCloudfrontAssetUrl(tenant?.tenantlogo)
  const isUnsubscribePage =
    location.pathname === '/unsubscribe' ||
    /^\/[^/]+\/unsubscribe(?:\/|$)/.test(location.pathname)
  // Use a ref for `t` so the nav effect doesn't re-run on every render
  // (t is a plain arrow function, new reference each render)
  const tRef = React.useRef(t)
  tRef.current = t
  // Same for handleSearchInput — it changes reference when navigate/tenanturlname change
  const handleSearchInputRef = React.useRef(null)
  const handleSearchSubmitRef = React.useRef(null)

  // Helper function to decode HTML entities
  const decodeHtmlEntities = (text) => {
    const textarea = document.createElement('textarea')
    textarea.innerHTML = text
    return textarea.value
  }

  // Handle language change - updates both frontend and backend
  const handleLanguageChange = React.useCallback(
    async (languageid, languagecode) => {
      if (tenanturlname) {
        window.dataLayer = window.dataLayer || []
        window.dataLayer.push({ event: 'cta_click', ctaName: 'language_change', language: languagecode })
        // Pass client info if authenticated to update backend
        await changeLanguage(
          { languageid, languagecode },
          tenanturlname,
          client?.clientId,
          tenant?.tenantid,
        )
      }
    },
    [tenanturlname, changeLanguage, client?.clientId, tenant?.tenantid],
  )

  // Auto-focus mobile search when shown
  React.useEffect(() => {
    if (showMobileSearch && mobileSearchRef.current) {
      mobileSearchRef.current.focus()
    }
  }, [showMobileSearch])

  // Close search when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        showMobileSearch &&
        mobileSearchRef.current &&
        !mobileSearchRef.current.contains(e.target) &&
        !e.target.closest('[data-search-trigger]')
      ) {
        setShowMobileSearch(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [showMobileSearch])

  // Fetch tenant by URL name when param is available or changes
  React.useEffect(() => {
    if (tenanturlname) {
      loadTenant(tenanturlname)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenanturlname])

  // Dynamic favicon — set immediately from URL tenanturlname, no API wait
  React.useEffect(() => {
    let link = document.querySelector("link[rel~='icon']")
    if (!link) {
      link = document.createElement('link')
      link.rel = 'icon'
      document.getElementsByTagName('head')[0].appendChild(link)
    }

    if (!tenanturlname) {
      link.href = "data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' width='16' height='16'><rect width='16' height='16' fill='white'/></svg>"
      return
    }

    const cdnFavicon = `${process.env.REACT_APP_CLOUDFRONT}favicons/${tenanturlname}.ico`
    const img = new Image()
    img.onload = () => {
      link.href = cdnFavicon
    }
    img.onerror = () => {
      link.href = "data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' width='16' height='16'><rect width='16' height='16' fill='white'/></svg>"
    }
    img.src = cdnFavicon
  }, [tenanturlname])

  // Dynamic browser tab title from tenant config (waits for API)
  React.useEffect(() => {
    if (!tenant?.tenantname) return
    document.title = tenant.tenantname
  }, [tenant?.tenantname])

  // Clear search query from URL on mount so that refresh shows all offers
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    if (params.get('search')) {
      // Clear URL search param
      navigate(location.pathname, { replace: true })
      // Ensure local state is empty
      setSearchQuery('')
      // Also update the permanent search input if it exists
      if (permanentSearchInputRef.current) {
        permanentSearchInputRef.current.value = ''
      }
    }
  }, [])

  // Update search state as user types (no navigation until submit)
  const handleSearchInput = React.useCallback((query) => {
    setSearchQuery(query)
  }, [])
  handleSearchInputRef.current = handleSearchInput

  // Navigate with search query - called on Enter or search button press
  const handleSearchSubmit = React.useCallback(
    (query) => {
      if (tenanturlname) {
        if (query.trim()) {
          window.dataLayer = window.dataLayer || []
          window.dataLayer.push({ event: 'cta_click', ctaName: 'search_submit', searchTerm: query.trim() })
          navigate(`/${tenanturlname}?search=${encodeURIComponent(query)}`, {
            replace: true,
          })
        } else {
          navigate(`/${tenanturlname}`, { replace: true })
        }
      }
    },
    [tenanturlname, navigate],
  )
  handleSearchSubmitRef.current = handleSearchSubmit

  // Check NPS eligibility for first_login trigger based on tenant config
  React.useEffect(() => {
    if (isAuthenticated && client?.clientId && !npsChecked) {
      // Get first_login delay from tenant config
      const npsConfig = tenant?.tenantmasterconfig?.nps_config
      if (!npsConfig?.enabled || !npsConfig?.triggers?.first_login?.enabled) {
        return
      }

      const triggerConfig = npsConfig.triggers.first_login
      // Use delay_amount/delay_unit for initial wait time (default: 120 seconds)
      // cooldown_amount/cooldown_unit is for preventing repeat shows after response
      const delayAmount = triggerConfig.delay_amount || 120
      const delayUnit = triggerConfig.delay_unit || 'seconds'

      // Convert to milliseconds
      const unitMultipliers = {
        seconds: 1000,
        minutes: 60 * 1000,
        hours: 60 * 60 * 1000,
        days: 24 * 60 * 60 * 1000,
      }
      const delayMs =
        delayAmount * (unitMultipliers[delayUnit] || unitMultipliers.seconds)

      const timer = setTimeout(async () => {
        setNpsChecked(true)
        try {
          const result = await checkEligibility(
            'first_login',
          )
          if (result?.eligible) {
            setShowNPS(true)
          }
        } catch (error) {
          console.error('NPS first_login: eligibility check failed:', error)
        }
      }, delayMs)

      return () => clearTimeout(timer)
    }
  }, [
    isAuthenticated,
    client?.clientId,
    npsChecked,
    checkEligibility,
    tenant?.tenantmasterconfig,
    tenant?.tenantid,
  ])

  // Position mobile kebab to the left of the logo on small screens, add a search icon on the right,
  // and add a simple language picker (English / 日本語) to the locale button in the drawer.
  React.useEffect(() => {
    if (typeof window === 'undefined') return
    const header = document.querySelector('header')
    if (!header) return

    // Select the desktop nav element (first nav in header)
    const mainNav = header.querySelector('nav.md\\:flex')
    const originalMainNavHtml = mainNav ? mainNav.innerHTML : null

    const getHamburger = () => header.querySelector('button[aria-expanded]')
    let localeMenu = null
    let desktopControlsWrapper = null

    const createPermanentSearchInput = () => {
      // Reuse existing input if it exists — preserve whatever the user has typed
      if (permanentSearchInputRef.current) {
        return permanentSearchInputRef.current
      }

      const permanentSearchInput = document.createElement('input')
      permanentSearchInput.type = 'text'
      permanentSearchInput.placeholder = tRef.current(
        'mainLayout.searchPlaceholder',
      )
      permanentSearchInput.setAttribute('data-permanent-search', 'true')
      permanentSearchInput.className =
        'flex h-[41px] rounded-[8px] border border-input bg-transparent px-2 text-sm transition-colors placeholder:text-gray-400 focus:outline-none focus:border-theme-primary disabled:cursor-not-allowed disabled:opacity-50 placeholder:text-[12px]'
      permanentSearchInput.setAttribute('autocomplete', 'off')
      permanentSearchInput.style.width = '100%'
      // Restore current search value when input is recreated after effect re-run
      permanentSearchInput.value = searchQuery || ''

      permanentSearchInput.addEventListener('input', (e) => {
        handleSearchInputRef.current(e.target.value)
      })

      permanentSearchInput.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault()
          handleSearchSubmitRef.current(e.target.value)
        }
      })

      permanentSearchInputRef.current = permanentSearchInput
      return permanentSearchInput
    }

    const findLocaleButtonInDrawer = () => {
      const aside = document.querySelector('aside')
      if (!aside) return null
      const buttons = Array.from(aside.querySelectorAll('button'))
      return (
        buttons.find((b) => {
          const txt = (b.textContent || '').trim()
          return txt.startsWith('English') || txt.startsWith('日本語')
        }) || null
      )
    }

    const showLocaleMenu = (btn) => {
      if (!btn) return
      hideLocaleMenu()

      const menu = document.createElement('div')
      menu.className = 'bg-white border rounded-md shadow z-[300] py-1'
      menu.setAttribute('role', 'menu')
      menu.style.position = 'absolute'
      menu.style.minWidth = `${Math.max(120, btn.offsetWidth)}px`
      menu.style.boxSizing = 'border-box'
      // start hidden so we can measure then reposition if needed
      menu.style.visibility = 'hidden'
      menu.style.left = '0px'
      menu.style.top = '0px'

      const languages = tenant?.languages || []

      languages.forEach((lang) => {
        const item = document.createElement('button')
        item.type = 'button'
        item.className =
          'w-full text-left px-3 py-2 text-sm hover:text-theme-primary hover:bg-theme-primary/10 focus:outline-none'
        item.setAttribute('role', 'menuitem')
        item.textContent =
          lang.languagenametranslate || lang.languagecode?.toUpperCase()
        item.addEventListener('click', (e) => {
          e.stopPropagation()
          handleLanguageChange(lang.languageid, lang.languagecode)
          const span = btn.querySelector('span')
          if (span)
            span.textContent =
              lang.languagenametranslate || lang.languagecode?.toUpperCase()
          else
            btn.textContent = `${
              lang.languagenametranslate || lang.languagecode?.toUpperCase()
            } ›`
          btn.textContent = `${
            lang.languagenametranslate || lang.languagecode?.toUpperCase()
          } ›`
          hideLocaleMenu()
        })
        menu.appendChild(item)
      })

      document.body.appendChild(menu)
      localeMenu = menu

      // position calculation after appended so sizes are known
      const rect = btn.getBoundingClientRect()
      const menuRect = menu.getBoundingClientRect()
      const gap = 8

      let left = rect.left
      // ensure menu doesn't overflow right edge
      if (left + menuRect.width > window.innerWidth - gap) {
        left = Math.max(gap, window.innerWidth - gap - menuRect.width)
      }
      // ensure menu doesn't overflow left edge
      if (left < gap) left = gap

      // prefer placing below the button
      let top = rect.bottom + gap
      // if it would overflow bottom, place above
      if (top + menuRect.height > window.innerHeight - gap) {
        const altTop = rect.top - gap - menuRect.height
        if (altTop >= gap) top = altTop
        // otherwise clamp to fit inside viewport
        else top = Math.max(gap, window.innerHeight - gap - menuRect.height)
      }

      menu.style.left = `${Math.round(left)}px`
      menu.style.top = `${Math.round(top)}px`
      menu.style.visibility = 'visible'

      const onDoc = (ev) => {
        if (!menu.contains(ev.target) && !btn.contains(ev.target))
          hideLocaleMenu()
      }
      document.addEventListener('click', onDoc)
      menu._onDoc = onDoc
    }

    const hideLocaleMenu = () => {
      if (localeMenu) {
        if (localeMenu._onDoc)
          document.removeEventListener('click', localeMenu._onDoc)
        localeMenu.remove()
        localeMenu = null
      }
    }

    const buildDesktopNavStatic = () => {
      if (!mainNav) {
        return
      }
      if (!tenant || !tenanturlname) {
        return
      }

      mainNav.classList.add('flex-1', 'items-center')
      mainNav.innerHTML = ''
      const t = tRef.current

      // Right side buttons wrapper
      const rightWrap = document.createElement('div')
      rightWrap.className = 'flex gap-1 whitespace-nowrap'
      rightWrap.style.width = '72%'
      rightWrap.style.justifyContent = 'space-around'

      const localeBtn = document.createElement('button')
      localeBtn.type = 'button'
      localeBtn.setAttribute('data-locale-btn', 'true')
      localeBtn.className =
        'px-2 py-2 rounded-md border text-sm hover:bg-theme-primary/10 focus:outline-none'
      const currentLang = tenant?.languages?.find(
        (l) => l.languagecode === languageCode,
      )
      const currentLangName =
        currentLang?.languagenametranslate ||
        languageCode?.toUpperCase() ||
        'Language'
      localeBtn.innerHTML = `<svg class="w-6 h-6 text-theme-primary" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" /><path d="M2 12h20M12 2a15.3 15.3 0 010 20M12 2a15.3 15.3 0 000 20" /></svg>`
      localeBtn.addEventListener('click', (e) => {
        e.stopPropagation()
        if (localeMenu) hideLocaleMenu()
        else showLocaleMenu(localeBtn)
      })

      rightWrap.appendChild(localeBtn)

      // Sign In / Sign Out button
      const authBtn1 = document.createElement('button')
      authBtn1.type = 'button'
      authBtn1.className =
        'px-2 py-2 w-auto rounded-md text-sm font-semibold btn-secondary hover:!translate-y-0'
      authBtn1.textContent = isAuthenticated
        ? t('mainLayout.signOut')
        : t('mainLayout.signIn')
      authBtn1.addEventListener('click', async () => {
        if (isAuthenticated) {
          await logout(tenant?.tenantid)
          navigate(`/${tenanturlname}/signin`, { replace: true })
        } else {
          navigate(`/${tenanturlname}/signin`, { replace: true })
        }
      })

      // Second button - Sign Up
      const authBtn2 = document.createElement('button')
      authBtn2.type = 'button'
      authBtn2.className =
        'px-2 py-2 w-auto rounded-md text-sm font-semibold text-white bg-theme-primary hover:bg-theme-primary/90 focus:outline-none'
      authBtn2.textContent = isAuthenticated
        ? t('mainLayout.signOut')
        : t('mainLayout.signUp')
      authBtn2.addEventListener('click', async () => {
        if (isAuthenticated) {
          window.dataLayer = window.dataLayer || []
          window.dataLayer.push({ event: 'cta_click', ctaName: 'logout' })
          await logout(tenant?.tenantid)
          navigate(`/${tenanturlname}/signin`, { replace: true })
        } else {
          navigate(`/${tenanturlname}/create-account`, { replace: true })
        }
      })

      // Add both buttons
      if (!isAuthenticated) {
        rightWrap.appendChild(authBtn1)
      }
      rightWrap.appendChild(authBtn2)

      // Create wrapper for search input and buttons - part of flex layout
      desktopControlsWrapper = document.createElement('div')
      desktopControlsWrapper.setAttribute('data-desktop-controls', 'true')
      desktopControlsWrapper.style.display = 'flex'
      desktopControlsWrapper.style.flexDirection = 'column-row'
      desktopControlsWrapper.style.gap = '4px'
      desktopControlsWrapper.style.alignItems = 'flex-end'
      desktopControlsWrapper.style.marginLeft = 'auto'
      desktopControlsWrapper.style.marginTop = '0px'
      desktopControlsWrapper.style.paddingRight = '0px'

      // Add search input to wrapper
      const searchInput = createPermanentSearchInput()
      desktopControlsWrapper.appendChild(searchInput)

      // Add buttons wrapper to controls wrapper
      desktopControlsWrapper.appendChild(rightWrap)

      // Append wrapper to mainNav (not header)
      mainNav.appendChild(desktopControlsWrapper)
    }

    const restoreDesktopNav = () => {
      if (!mainNav) return
      if (originalMainNavHtml !== null) mainNav.innerHTML = originalMainNavHtml

      // Clear the search input ref when restoring
      permanentSearchInputRef.current = null

      // Remove the desktop controls wrapper when restoring
      if (desktopControlsWrapper && desktopControlsWrapper.parentElement) {
        desktopControlsWrapper.remove()
        desktopControlsWrapper = null
      }
    }

    const applyPosition = () => {
      const btn = getHamburger()
      if (!btn) return
      const isMobile = window.matchMedia('(max-width: 767px)').matches

      if (isMobile) {
        btn.classList.add('absolute', 'left-3', 'top-4')
        header.classList.add('pl-0', 'relative')

        const drawerLocale = findLocaleButtonInDrawer()
        if (drawerLocale && !drawerLocale.dataset._attached) {
          drawerLocale.dataset._attached = '1'
          drawerLocale.setAttribute('data-locale-btn', 'true')
          drawerLocale.classList.add('w-full', 'text-left')
          drawerLocale.addEventListener('click', (e) => {
            e.stopPropagation()
            if (localeMenu) hideLocaleMenu()
            else showLocaleMenu(drawerLocale)
          })
        }

        restoreDesktopNav()
      } else {
        // Desktop view: show permanent search input
        btn.classList.remove('absolute', 'left-3', 'top-3')
        header.classList.remove('relative')

        buildDesktopNavStatic()
      }
    }

    applyPosition()
    window.addEventListener('resize', applyPosition)

    return () => {
      window.removeEventListener('resize', applyPosition)
      // Do NOT null permanentSearchInputRef here — the value must survive effect re-runs.
      // restoreDesktopNav (called on mobile switch) clears it when appropriate.
      if (desktopControlsWrapper && desktopControlsWrapper.parentElement)
        desktopControlsWrapper.remove()
      hideLocaleMenu()
      restoreDesktopNav()
      header.classList.remove('pl-0', 'relative')
      const btn = getHamburger()
      if (btn) btn.classList.remove('absolute', 'left-3', 'top-3')
    }
  }, [
    tenant,
    languageCode,
    isAuthenticated,
    tenanturlname,
    navigate,
    // t is intentionally excluded — stored in tRef to avoid re-running on every render
    // handleSearchInput is intentionally excluded — stored in handleSearchInputRef
    handleLanguageChange,
    logout,
  ])

  // Prevent body scroll when drawer is open and handle ESC to close
  React.useEffect(() => {
    document.body.classList.toggle('overflow-hidden', drawerOpen)

    const onKey = (e) => {
      if (e.key === 'Escape') {
        if (drawerOpen) setDrawerOpen(false)
        if (showMobileSearch) setShowMobileSearch(false)
      }
    }
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.classList.remove('overflow-hidden')
    }
  }, [drawerOpen, showMobileSearch])

  const handleNavClick = () => setDrawerOpen(false)

  // Show loading screen while tenant data is loading
  if (tenantLoading) {
    return (
      <Loader
        fullscreen
        label={t('common.loading')}
        size="xl"
        overlayClassName="bg-white"
        labelColor="text-gray-700"
      />
    )
  }

  // If tenant lookup failed (invalid slug), show generic unbranded 404
  if (tenantError && !tenant?.tenantid) {
    return <GenericNotFound />
  }

  if (isUnsubscribePage) {
    return (
      <div className="flex min-h-screen flex-col">
        <main className="flex-1 w-full">
          <Outlet />
        </main>
      </div>
    )
  }

  return (
    <div className="flex flex-col min-h-screen">
      {/* Global Language Change Loader */}
      {/* {isChangingLanguage && (
        <Loader 
          fullscreen
          size="xl"
          labelColor="text-gray-700"
        />
      )} */}

      <header
        className="relative w-full max-w-5xl mx-auto bg-white border-b border-gray-200 "
        style={{ position: 'relative' }}
      >
        <div className="max-w-5xl lg:px-0 sm:px-6">
          <div className="flex items-center h-16 md:h-auto py-1">
            <div className="flex items-center gap-4 ml-10 md:ml-0 ">
              <button
                className="flex items-center focus:outline-none active:opacity-100 ml-[4px]"
                aria-label="Go to home page"
                onClick={() => navigate(`/${tenanturlname}`)}
                style={{
                  WebkitTapHighlightColor: 'transparent',
                  userSelect: 'none',
                  outline: 'none',
                }}
                onMouseDown={(e) => e.preventDefault()}
              >
                {tenantLogoUrl ? (
                  <img
                    src={tenantLogoUrl}
                    alt={tenant?.tenantname || ''}
                    className="h-6 w-auto pointer-events-none select-none"
                    style={{
                      userSelect: 'none',
                      WebkitUserDrag: 'none',
                      opacity: 1,
                      filter: 'none',
                    }}
                    draggable="false"
                  />
                ) : (
                  <span className="text-xl font-bold text-theme-primary">
                    {tenant?.tenantname || 'Logo'}
                  </span>
                )}
              </button>
            </div>

            {/* Desktop nav (visible md+) */}
            <nav
              className="md:flex flex-1 items-center"
              aria-label={t('mainLayout.navigationMenu')}
            ></nav>

            {/* Mobile: Search icon on right */}
            <div className="md:hidden absolute right-3 top-3">
              <button
                data-search-trigger
                onClick={() => setShowMobileSearch((s) => !s)}
                className="p-2 rounded-md hover:bg-gray-100 focus:outline-none"
                aria-label={t('mainLayout.searchPlaceholder')}
              >
                <Search className="w-4 h-4" />
              </button>
            </div>

            {/* Mobile hamburger (visible < md) */}
            <div className="md:hidden">
              <button
                className="inline-flex items-center justify-center rounded-md p-2 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                aria-label={
                  drawerOpen
                    ? t('mainLayout.closeMenu')
                    : t('mainLayout.openMenu')
                }
                aria-expanded={drawerOpen}
                onClick={() => setDrawerOpen((s) => !s)}
              >
                {drawerOpen ? (
                  <span aria-hidden="true" className="text-xl">
                    ✕
                  </span>
                ) : (
                  <HamburgerIcon className="w-[16px]" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Search Bar Popup */}
        {showMobileSearch && (
          <input
            ref={mobileSearchRef}
            type="text"
            placeholder={t('mainLayout.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => handleSearchInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                handleSearchSubmit(searchQuery)
                setShowMobileSearch(false)
              }
            }}
            className="absolute right-3 top-3 flex h-[36px] w-[178px] rounded-[8px] border border-input bg-white px-2 text-sm transition-colors placeholder:text-gray-400 focus:outline-none focus:border-theme-primary placeholder:text-[12px] z-10"
            autoComplete="off"
          />
        )}
      </header>

      <div className="sub-header relative w-full max-w-5xl mx-auto bg-white border-gray-200 hidden md:block ">
        <div className="max-w-5xl lg:px-0 sm:px-6">
          <div className="flex items-center h-16 md:h-auto pb-1">
            {/* Desktop nav (visible md+) */}
            <nav
              className="hidden md:flex flex-1 items-center"
              aria-label={t('mainLayout.navigationMenu')}
            >
              <div className="flex items-center gap-2">
                <button
                  onClick={() => navigate(`/${tenanturlname}`)}
                  className="inline-flex items-center gap-1 text-xs font-normal hover:text-theme-primary border-b-2 border-transparent
hover:border-theme-primary transition-colors duration-150 px-2 py-3"
                >
                  <span
                    className="w-6 h-6 flex items-center justify-center"
                    aria-hidden="true"
                  >
                    <ShoppingBagIcon className="w-[18px] h-[18px] text-theme-primary" />
                  </span>
                  <span>{t('mainLayout.offers')}</span>
                </button>
                {!isAuthenticated &&
                  tenant?.tenantmasterconfig?.howtoregisterpage?.show !==
                    false && (
                    <button
                      onClick={() => navigate(`/${tenanturlname}/about`)}
                      className="inline-flex items-center gap-1 text-xs font-normal hover:text-theme-primary border-b-2 border-transparent
hover:border-theme-primary transition-colors duration-150 px-2 py-3"
                    >
                      <span
                        className="w-6 h-6 flex items-center justify-center"
                        aria-hidden="true"
                      >
                        <MonitorCheckIcon className="w-[18px] h-[18px] text-theme-primary" />
                      </span>
                      <span>{t('mainLayout.howToRegister')}</span>
                    </button>
                  )}
                {isAuthenticated && (
                  <>
                    <button
                      onClick={() =>
                        navigate(`/${tenanturlname}/myprofile?tab=my-cards`)
                      }
                      className="hidden items-center gap-1 text-xs font-normal hover:text-theme-primary border-b-2 border-transparent
hover:border-theme-primary transition-colors duration-150 px-2 py-3"
                    >
                      <span
                        className="w-6 h-6 flex items-center justify-center"
                        aria-hidden="true"
                      >
                        <UserIcon className="w-[18px] h-[18px] text-theme-primary" />
                      </span>
                      <span>{t('mainLayout.profile')}</span>
                    </button>
                    <button
                      onClick={() =>
                        navigate(`/${tenanturlname}/myprofile?tab=my-cards`)
                      }
                      className="inline-flex items-center gap-1 text-xs font-normal hover:text-theme-primary border-b-2 border-transparent
hover:border-theme-primary transition-colors duration-150 px-2 py-3"
                    >
                      <span
                        className="w-6 h-6 flex items-center justify-center"
                        aria-hidden="true"
                      >
                        <DoubleCard className="w-[18px] h-[18px] text-theme-primary" />
                      </span>
                      <span>{t('mainLayout.myCards')}</span>
                    </button>
                    <button
                      onClick={() =>
                        navigate(`/${tenanturlname}/myprofile?tab=cashback`)
                      }
                      className="inline-flex items-center gap-1 text-xs font-normal hover:text-theme-primary border-b-2 border-transparent
hover:border-theme-primary transition-colors duration-150 px-2 py-3"
                    >
                      <span
                        className="w-6 h-6 flex items-center justify-center"
                        aria-hidden="true"
                      >
                        <CashOnHand className="w-[18px] h-[18px] text-theme-primary" />
                      </span>
                      <span>{t('mainLayout.cashbackOverview')}</span>
                    </button>

                    <button
                      onClick={() => {
                        navigate(`/${tenanturlname}/account-settings`)
                        handleNavClick()
                      }}
                      className="inline-flex items-center gap-1 text-xs font-normal hover:text-theme-primary border-b-2 border-transparent
hover:border-theme-primary transition-colors duration-150 px-2 py-3"
                    >
                      <span
                        className="w-6 h-6 flex items-center justify-center"
                        aria-hidden="true"
                      >
                        <Settings className="w-[18px] h-[18px] text-theme-primary" />
                      </span>
                      <span>{t('mainLayout.accountSettings')}</span>
                    </button>
                  </>
                )}
                {showFaq && (
                  <button
                    onClick={() => navigate(`/${tenanturlname}/faq`)}
                    className="inline-flex items-center gap-1 text-xs font-normal hover:text-theme-primary border-b-2 border-transparent
hover:border-theme-primary transition-colors duration-150 px-2 py-3"
                  >
                    <span
                      className="w-6 h-6 flex items-center justify-center"
                      aria-hidden="true"
                    >
                      <QuestionMark className="w-[18px] h-[18px] text-theme-primary" />
                    </span>
                    <span>{t('mainLayout.faq')}</span>
                  </button>
                )}
                {/* <button
                  onClick={() => navigate(`/${tenanturlname}/terms`)}
                  className="inline-flex items-center gap-1 text-xs font-normal hover:text-theme-primary border-b-2 border-transparent
hover:border-theme-primary transition-colors duration-150 px-2 py-3"
                >
                  <span
                    className="w-6 h-6 flex items-center justify-center"
                    aria-hidden="true"
                  >
                    <ListIcon className="w-[18px] h-[18px] text-theme-primary" />
                  </span>
                  <span>
                    {decodeHtmlEntities(t("mainLayout.termsConditions"))}
                  </span>
                </button> */}
              </div>
            </nav>

            {/* Mobile hamburger (visible < md) */}
            <div className="md:hidden">
              <button
                className="inline-flex items-center justify-center rounded-md p-2 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                aria-label={
                  drawerOpen
                    ? t('mainLayout.closeMenu')
                    : t('mainLayout.openMenu')
                }
                aria-expanded={drawerOpen}
                onClick={() => setDrawerOpen((s) => !s)}
              >
                {drawerOpen ? (
                  <span aria-hidden="true" className="text-xl">
                    ✕
                  </span>
                ) : (
                  <HamburgerIcon className="w-[16px]" />
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
      {/* Overlay */}
      <div
        className={`fixed inset-0 bg-black/40 transition-opacity duration-200 z-40 ${
          drawerOpen
            ? 'opacity-100 pointer-events-auto'
            : 'opacity-0 pointer-events-none'
        }`}
        onClick={() => setDrawerOpen(false)}
        aria-hidden={!drawerOpen}
      />
      {/* Mobile Drawer (slides from left) */}
      <aside
        className={`fixed left-0 top-0 h-full w-80 max-w-full bg-white shadow-lg z-[200] transform transition-transform duration-300 ${
          drawerOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        role="dialog"
        aria-modal="true"
        aria-hidden={!drawerOpen}
      >
        <div className="h-full flex flex-col">
          <div className="px-4 py-4 border-b border-gray-100 flex items-center justify-between">
            <div>
              {tenantLogoUrl ? (
                <img
                  src={tenantLogoUrl}
                  alt={tenant?.tenantname || 'Logo'}
                  className="h-6 w-auto"
                />
              ) : (
                <span className="text-xl font-bold text-theme-primary">
                  {tenant?.tenantname || 'Logo'}
                </span>
              )}
            </div>
            <button
              ref={closeBtnRef}
              className="p-2 rounded-md hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
              aria-label={t('mainLayout.closeMenu')}
              onClick={() => setDrawerOpen(false)}
            >
              ✕
            </button>
          </div>

          <nav
            aria-label={t('mainLayout.navigationMenu')}
            className="flex-1 overflow-auto"
          >
            <ul className="space-y-1 p-4">
              <li>
                <button
                  onClick={() => {
                    navigate(`/${tenanturlname}`)
                    handleNavClick()
                  }}
                  className="group flex items-center gap-3 rounded-md px-3 py-2 hover:bg-theme-primary/10 transition-colors duration-150 w-full text-left"
                >
                  <span
                    className="w-6 h-6 flex items-center justify-center text-gray-400 group-hover:text-theme-primary"
                    aria-hidden="true"
                  >
                    <ShoppingBagIcon className="w-[18px] h-[18px] text-theme-primary" />
                  </span>
                  <span className="text-base font-normal text-theme-secondary group-hover:text-theme-primary">
                    {t('mainLayout.offers')}
                  </span>
                </button>
              </li>

              {!isAuthenticated &&
                tenant?.tenantmasterconfig?.howtoregisterpage?.show !==
                  false && (
                  <li>
                    <button
                      onClick={() => {
                        navigate(`/${tenanturlname}/about`)
                        handleNavClick()
                      }}
                      className="group flex items-center gap-3 rounded-md px-3 py-2 hover:bg-theme-primary/10 transition-colors duration-150 w-full text-left"
                    >
                      <span
                        className="w-6 h-6 flex items-center justify-center text-gray-400 group-hover:text-theme-primary"
                        aria-hidden="true"
                      >
                        <MonitorCheckIcon className="w-[18px] h-[18px] text-theme-primary" />
                      </span>
                      <span className="text-base font-normal text-theme-secondary group-hover:text-theme-primary">
                        {t('mainLayout.howToRegister')}
                      </span>
                    </button>
                  </li>
                )}

              {isAuthenticated && (
                <>
                  <li className="hidden">
                    <button
                      onClick={() => {
                        navigate(`/${tenanturlname}/myprofile?tab=my-cards`)
                        handleNavClick()
                      }}
                      className="group flex items-center gap-3 rounded-md px-3 py-2 hover:bg-theme-primary/10 transition-colors duration-150 w-full text-left"
                    >
                      <span
                        className="w-6 h-6 flex items-center justify-center text-gray-400 group-hover:text-theme-primary"
                        aria-hidden="true"
                      >
                        <UserIcon className="w-[18px] h-[18px] text-theme-primary" />
                      </span>
                      <span className="text-base font-normal text-theme-secondary group-hover:text-theme-primary">
                        {t('mainLayout.myProfile')}
                      </span>
                    </button>
                  </li>

                  <li>
                    <button
                      onClick={() => {
                        navigate(`/${tenanturlname}/myprofile?tab=my-cards`)
                        handleNavClick()
                      }}
                      className="group flex items-center gap-3 rounded-md px-3 py-2 hover:bg-theme-primary/10 transition-colors duration-150 w-full text-left"
                    >
                      <span
                        className="w-6 h-6 flex items-center justify-center text-gray-400 group-hover:text-theme-primary"
                        aria-hidden="true"
                      >
                        <DoubleCard className="w-[18px] h-[18px] text-theme-primary" />
                      </span>
                      <span className="text-base font-normal text-theme-secondary group-hover:text-theme-primary">
                        {t('mainLayout.myCards')}
                      </span>
                    </button>
                  </li>

                  <li>
                    <button
                      onClick={() => {
                        navigate(`/${tenanturlname}/myprofile?tab=cashback`)
                        handleNavClick()
                      }}
                      className="group flex items-center gap-3 rounded-md px-3 py-2 hover:bg-theme-primary/10 transition-colors duration-150 w-full text-left"
                    >
                      <span
                        className="w-6 h-6 flex items-center justify-center text-gray-400 group-hover:text-theme-primary"
                        aria-hidden="true"
                      >
                        <CashOnHand className="w-[18px] h-[18px] text-theme-primary" />
                      </span>
                      <span className="text-base font-normal text-theme-secondary group-hover:text-theme-primary">
                        {t('mainLayout.cashbackOverview')}
                      </span>
                    </button>
                  </li>
                </>
              )}

              <li className="h-px bg-gray-100 my-2" aria-hidden="true"></li>

              {showFaq && (
                <li>
                  <button
                    onClick={() => {
                      navigate(`/${tenanturlname}/faq`)
                      handleNavClick()
                    }}
                    className="group flex items-center gap-3 rounded-md px-3 py-2 hover:bg-theme-primary/10 transition-colors duration-150 w-full text-left"
                  >
                    <span
                      className="w-6 h-6 flex items-center justify-center text-gray-400 group-hover:text-theme-primary"
                      aria-hidden="true"
                    >
                      <QuestionMark className="w-[18px] h-[18px] text-theme-primary" />
                    </span>
                    <span className="text-base font-normal text-theme-secondary group-hover:text-theme-primary">
                      {t('mainLayout.faq')}
                    </span>
                  </button>
                </li>
              )}

              {/* <li>
                <button
                  onClick={() => {
                    navigate(`/${tenanturlname}/terms`);
                    handleNavClick();
                  }}
                  className="group flex items-center gap-3 rounded-md px-3 py-2 hover:bg-theme-primary/10 transition-colors duration-150 w-full text-left"
                >
                  <span
                    className="w-6 h-6 flex items-center justify-center text-gray-400 group-hover:text-theme-primary"
                    aria-hidden="true"
                  >
                    <ListIcon className="w-[18px] h-[18px] text-theme-primary" />
                  </span>
                  <span className="text-base font-normal text-theme-secondary group-hover:text-theme-primary">
                    {decodeHtmlEntities(t("mainLayout.termsConditions"))}
                  </span>
                </button>
              </li> */}
              <li className="h-px bg-gray-100 my-2" aria-hidden="true"></li>
              {isAuthenticated && (
                <>
                  <li>
                    <button
                      onClick={() => {
                        navigate(`/${tenanturlname}/account-settings`)
                        handleNavClick()
                      }}
                      className="group flex items-center gap-3 rounded-md px-3 py-2 hover:bg-theme-primary/10 transition-colors duration-150 w-full text-left"
                    >
                      <span
                        className="w-6 h-6 flex items-center justify-center text-gray-400 group-hover:text-theme-primary"
                        aria-hidden="true"
                      >
                        <Settings className="w-[18px] h-[18px] text-theme-primary" />
                      </span>
                      <span className="text-base font-normal text-theme-secondary group-hover:text-theme-primary">
                        {t('mainLayout.accountSettings')}
                      </span>
                    </button>
                  </li>

                  <li>
                    <button
                      onClick={async () => {
                        window.dataLayer = window.dataLayer || []
                        window.dataLayer.push({ event: 'cta_click', ctaName: 'logout' })
                        handleNavClick()
                        await logout(tenant?.tenantid)
                        navigate(`/${tenanturlname}/signin`, { replace: true })
                      }}
                      className="group flex items-center gap-3 rounded-md px-3 py-2 hover:bg-theme-primary/10 transition-colors duration-150 w-full text-left"
                    >
                      <span
                        className="w-6 h-6 flex items-center justify-center text-red-400 group-hover:text-red-600"
                        aria-hidden="true"
                      >
                        <SignOut className="w-[18px] h-[18px] text-theme-primary" />
                      </span>
                      <span className="text-base font-normal text-theme-primary group-hover:text-red-600">
                        {t('mainLayout.signOut')}
                      </span>
                    </button>
                  </li>
                </>
              )}
            </ul>
          </nav>

          <div className="p-4">
            <button
              className="mt-3 w-full flex items-center justify-between rounded-md border px-3 py-2 text-sm hover:bg-theme-secondary/10"
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()

                const menu = document.createElement('div')
                menu.className =
                  'fixed inset-0 bg-black/40 flex items-center justify-center'
                menu.style.zIndex = '9999'
                menu.addEventListener('click', () => menu.remove())

                const dialog = document.createElement('div')
                dialog.className =
                  'bg-white rounded-lg shadow-xl max-w-sm w-full m-4 max-h-[80vh] overflow-auto'
                dialog.addEventListener('click', (e) => e.stopPropagation())

                const header = document.createElement('div')
                header.className =
                  'px-4 py-3 border-b flex items-center justify-between'
                header.innerHTML = `<h3 class="text-lg font-medium">${t(
                  'mainLayout.languageSelector',
                )}</h3><button class="p-1 hover:bg-gray-100 rounded" onclick="this.closest('.fixed').remove()">✕</button>`

                const list = document.createElement('div')
                list.className = 'py-2'
                ;(tenant?.languages || []).forEach((lang) => {
                  const item = document.createElement('button')
                  item.type = 'button'
                  item.className =
                    'w-full text-left px-4 py-3 hover:bg-theme-primary/10 flex items-center justify-between'
                  item.innerHTML = `<span>${
                    lang.languagenametranslate ||
                    lang.languagecode?.toUpperCase()
                  }</span>${
                    lang.languagecode === languageCode
                      ? '<span class="text-theme-primary">✓</span>'
                      : ''
                  }`
                  item.addEventListener('click', () => {
                    handleLanguageChange(lang.languageid, lang.languagecode)
                    menu.remove()
                  })
                  list.appendChild(item)
                })

                dialog.appendChild(header)
                dialog.appendChild(list)
                menu.appendChild(dialog)
                document.body.appendChild(menu)
              }}
            >
              <div className="flex items-center gap-2">
                <img
                  src={LanguageChangeIcon}
                  alt={t('mainLayout.changeLanguage')}
                  className="w-[25px] h-[25px]"
                />
                <span className="text-sm font-medium">
                  {tenant?.languages?.find(
                    (l) => l.languagecode === languageCode,
                  )?.languagenametranslate ||
                    languageCode?.toUpperCase() ||
                    t('mainLayout.language')}
                </span>
              </div>
              <span className="text-gray-400">›</span>
            </button>
          </div>

          <BottomAction
            isAuthenticated={isAuthenticated}
            tenanturlname={tenanturlname}
            onNavClick={handleNavClick}
          />
        </div>
      </aside>
      <main
        className="flex-1 w-full pb-8 xl:pb-12 max-w-5xl mx-auto"
        aria-hidden={drawerOpen}
      >
        <Outlet />
      </main>
      {/* FOOTER - START */}
      <footer className="mt-auto">
        <Footer />

        {/* NPS Feedback Modal */}
        {showNPS && (
          <FeedbackModal
            open={showNPS}
            onClose={() => setShowNPS(false)}
            triggerType="first_login"
            channel="IN_APP"
          />
        )}
      </footer>
      {/* FOOTER - END */}
      <Navigation isAuthenticated={isAuthenticated} />
    </div>
  )
}

export default MainLayout
